"use client";

import React from "react";
import { Calendar, User, Clock, Share2, ChevronLeft } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";

function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div data-section-id="887" className="w-full max-w-4xl mx-auto px-4 py-12 space-y-8">
        <div className="space-y-4 text-center">
          <div className="h-4 bg-muted/20 rounded w-32 mx-auto animate-pulse" />
          <div className="h-12 bg-muted/20 rounded-md w-3/4 mx-auto animate-pulse" />
        </div>
        <div className="aspect-video bg-muted/20 rounded-xl animate-pulse w-full" />
        <div className="space-y-4 max-w-3xl mx-auto">
          <div className="h-4 bg-muted/20 rounded w-full animate-pulse" />
          <div className="h-4 bg-muted/20 rounded w-full animate-pulse" />
          <div className="h-4 bg-muted/20 rounded w-2/3 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full min-h-[50vh] flex flex-col items-center justify-center text-center px-4 space-y-4">
        <p className="text-destructive font-medium">Unable to load the article.</p>
        <Button onClick={refetch} variant="outline" className="border-primary text-primary hover:bg-primary/10">Try Again</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="w-full min-h-[50vh] flex flex-col items-center justify-center text-center px-4 space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-serif text-foreground">Article Not Found</h1>
          <p className="text-muted-foreground">The compliance insight or industry update you are looking for has been moved.</p>
        </div>
        <Link to="/HomePage">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90">Return Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const date = post.date ? new Date(post.date).toLocaleDateString("en-US", {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : null;

  return (
    <article className="w-full max-w-5xl mx-auto px-4 py-12 md:py-20">
      {}
      <div className="mb-8 md:mb-12 text-center md:text-left">
        <Link to="/HomePage" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary transition-colors">
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Insights
        </Link>
      </div>

      {}
      <header className="mb-10 md:mb-14 text-center space-y-6 max-w-4xl mx-auto">
        <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
            {post.categories && post.categories.length > 0 && (
                <span className="uppercase tracking-widest text-xs font-bold text-primary bg-primary/10 px-3 py-1 rounded-full border border-primary/20">
                    {post.categories[0].name}
                </span>
            )}
            {date && (
                <span className="flex items-center gap-1.5 font-medium">
                    <Calendar className="w-4 h-4 opacity-70" />
                    {date}
                </span>
            )}
        </div>

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-foreground leading-[1.15] tracking-tight">
          {post.title}
        </h1>

        {post.excerpt && (
            <div 
                className="text-lg md:text-xl text-muted-foreground/80 max-w-2xl mx-auto leading-relaxed font-normal"
                dangerouslySetInnerHTML={{ __html: post.excerpt }} 
            />
        )}
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 md:mb-16 rounded-2xl overflow-hidden shadow-2xl shadow-primary/10 bg-muted border border-border/50">
          <AspectRatio ratio={16 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105"
            />
          </AspectRatio>
        </div>
      )}

      {}
      <div className="max-w-3xl mx-auto mb-16">
        <div 
          className="
            text-foreground/80 leading-relaxed text-lg
            [&_p]:mb-6 [&_p]:font-sans
            [&_h2]:text-3xl [&_h2]:font-serif [&_h2]:font-bold [&_h2]:mt-12 [&_h2]:mb-6 [&_h2]:text-foreground [&_h2]:tracking-tight
            [&_h3]:text-2xl [&_h3]:font-serif [&_h3]:font-semibold [&_h3]:mt-10 [&_h3]:mb-4 [&_h3]:text-foreground
            [&_h4]:text-xl [&_h4]:font-semibold [&_h4]:mt-8 [&_h4]:mb-3
            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ul]:space-y-2
            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 [&_ol]:space-y-2
            [&_li]:pl-1
            [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:py-4 [&_blockquote]:my-10 [&_blockquote]:italic [&_blockquote]:text-xl [&_blockquote]:text-foreground [&_blockquote]:bg-primary/5 [&_blockquote]:rounded-r-xl
            [&_img]:rounded-2xl [&_img]:shadow-lg [&_img]:my-10 [&_img]:w-full [&_img]:border [&_img]:border-border/50
            [&_figure]:my-10 [&_figure]:mx-0
            [&_figcaption]:text-center [&_figcaption]:text-sm [&_figcaption]:text-muted-foreground [&_figcaption]:mt-3
            [&_a]:text-primary [&_a]:font-semibold [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary/80
            [&_hr]:my-12 [&_hr]:border-border
          "
          dangerouslySetInnerHTML={{ __html: post.content || "" }} 
        />
      </div>

      <div className="max-w-3xl mx-auto">
        <Separator className="mb-12 bg-border/60" />

        {}
        {post.author && (
          <div className="bg-card rounded-2xl p-8 md:p-12 border border-border flex flex-col md:flex-row items-center md:items-start gap-8 text-center md:text-left shadow-sm">
            <Avatar className="w-24 h-24 border-4 border-background shadow-md shrink-0">
              <AvatarImage src={post.author.avatar?.['96'] || ""} alt={post.author.name} />
              <AvatarFallback className="text-2xl bg-primary text-primary-foreground font-serif font-bold">
                  {post.author.name.charAt(0)}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 space-y-4">
              <div>
                <h3 className="text-xl font-serif font-bold text-foreground mb-1">
                    About {post.author.name}
                </h3>
                <p className="text-sm text-primary uppercase tracking-widest font-bold">
                    Industry Solutions Expert
                </p>
              </div>
              
              <p className="text-muted-foreground leading-relaxed font-normal">
                  {post.author.name} specializes in cannabis compliance workflows, retail technology, and scaling enterprise operations. 
                  As part of the Cannabis Cloud team, they provide strategic guidance for dispensaries navigating the complexities of the regulated market.
              </p>

              {post.author.link && (
                  <div className="pt-2">
                    <Link to={post.author.link}>
                        <Button variant="outline" className="border-primary text-primary hover:bg-primary/5 rounded-full px-6">
                            View Expert Profile
                        </Button>
                    </Link>
                  </div>
              )}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && (window as any).wvcClient 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : {};
    
  
  if (!currentWPQueryParams.post_type) {
    currentWPQueryParams.post_type = 'post';
  }

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background w-full min-h-screen dark">
      <PostsProvider wp_query={wp_query} className="w-full">
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}